import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { TracerService } from '../../../services/tracer.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { TicketSkill, JsonSchemaNode } from '../../../models/ticket.model';
import { CanComponentDeactivate } from '../../../guards/unsaved-changes.guard';

type ViewMode = 'list' | 'create' | 'edit';

@Component({
  selector: 'app-skills-manager',
  imports: [NgClass, FormsModule, DatePipe],
  templateUrl: './skills-manager.html',
})
export class SkillsManager implements OnInit, CanComponentDeactivate {
  mode: ViewMode = 'list';
  skills: TicketSkill[] = [];
  loading = false;
  listError: string | null = null;

  // Edit form state
  editing: Partial<TicketSkill> = this.blankSkill();
  schemaText = '';
  saveError: string | null = null;
  saving = false;

  // Snapshot of the form when the editor was opened, to detect unsaved edits.
  private editorSnapshot = '';

  agRealm: string | null = null;

  constructor(
    private tracer: TracerService,
    private ls: LocalStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.agRealm = this.ls.getItem('agRealm');
    if (this.agRealm !== 'admins') {
      // Client-side belt-and-braces — backend already rejects non-admins,
      // but bouncing here avoids a fetch + error flash.
      this.router.navigateByUrl('/');
      return;
    }
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    this.listError = null;
    this.tracer.listSkills(false).subscribe({
      next: res => {
        this.skills = res.skills || [];
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.listError = err?.error?.error || 'Failed to load skills.';
      },
    });
  }

  startCreate(): void {
    this.editing = this.blankSkill();
    this.schemaText = JSON.stringify(this.defaultSchema(), null, 2);
    this.saveError = null;
    this.mode = 'create';
    this.editorSnapshot = this.formState();
  }

  startEdit(skill: TicketSkill): void {
    this.editing = { ...skill };
    this.schemaText = JSON.stringify(skill.output_schema, null, 2);
    this.saveError = null;
    this.mode = 'edit';
    this.editorSnapshot = this.formState();
  }

  cancelEdit(): void {
    this.editing = this.blankSkill();
    this.schemaText = '';
    this.saveError = null;
    this.mode = 'list';
    this.editorSnapshot = '';
  }

  save(): void {
    let parsedSchema: JsonSchemaNode;
    try {
      parsedSchema = JSON.parse(this.schemaText) as JsonSchemaNode;
    } catch (e) {
      this.saveError = 'Output schema is not valid JSON: ' + (e as Error).message;
      return;
    }
    if (!this.editing.name || !this.editing.system_prompt) {
      this.saveError = 'Name and system prompt are required.';
      return;
    }

    const payload: Partial<TicketSkill> = {
      name: this.editing.name,
      description: this.editing.description,
      system_prompt: this.editing.system_prompt,
      output_schema: parsedSchema,
      enabled: this.editing.enabled ?? true,
    };

    this.saving = true;
    this.saveError = null;

    const request =
      this.mode === 'edit' && this.editing.id !== undefined
        ? this.tracer.updateSkill(this.editing.id, payload)
        : this.tracer.createSkill(payload);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.cancelEdit();
        this.refresh();
      },
      error: err => {
        this.saving = false;
        this.saveError = err?.error?.error || 'Save failed.';
      },
    });
  }

  toggleEnabled(skill: TicketSkill): void {
    const patch: Partial<TicketSkill> = { ...skill, enabled: !skill.enabled };
    this.tracer.updateSkill(skill.id, patch).subscribe({
      next: () => this.refresh(),
      error: err => (this.listError = err?.error?.error || 'Toggle failed.'),
    });
  }

  /**
   * Soft action: disable (or re-enable) the skill, keeping the row and any
   * cached tickets. Backed by deleteSkill(hard=false) for disable and
   * updateSkill for re-enable, mirroring the list checkbox.
   */
  toggleDisabled(skill: TicketSkill): void {
    if (skill.enabled) {
      this.tracer.deleteSkill(skill.id, false).subscribe({
        next: () => this.refresh(),
        error: err => (this.listError = err?.error?.error || 'Disable failed.'),
      });
    } else {
      this.toggleEnabled(skill);
    }
  }

  /** Destructive action: drop the row outright (hard delete). */
  deletePermanently(skill: TicketSkill): void {
    if (!confirm(`Permanently delete the template "${skill.name}"? This cannot be undone.`)) return;
    this.tracer.deleteSkill(skill.id, true).subscribe({
      next: () => this.refresh(),
      error: err => (this.listError = err?.error?.error || 'Delete failed.'),
    });
  }

  // --- unsaved-changes tracking -------------------------------------

  /** Serialise the editor fields for cheap comparison against a snapshot. */
  private formState(): string {
    return JSON.stringify({
      name: this.editing.name ?? '',
      description: this.editing.description ?? '',
      system_prompt: this.editing.system_prompt ?? '',
      enabled: this.editing.enabled ?? true,
      schemaText: this.schemaText,
    });
  }

  /** True when the create/edit editor is open and its content has changed. */
  hasUnsavedChanges(): boolean {
    if (this.mode === 'list') return false;
    return this.formState() !== this.editorSnapshot;
  }

  private blankSkill(): Partial<TicketSkill> {
    return {
      name: '',
      description: '',
      system_prompt: '',
      output_schema: this.defaultSchema(),
      enabled: true,
    };
  }

  private defaultSchema(): JsonSchemaNode {
    return {
      type: 'object',
      properties: {
        title: { type: 'string' },
        description: { type: 'string' },
      },
      required: ['title', 'description'],
    };
  }
}
